import type { FormGrantRole, FormUserGrantDto } from "../../types/formGrant";

const GRANT_ROLES = new Set<FormGrantRole>(["manage", "view"]);

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function readId(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function isFormGrantRole(value: unknown): value is FormGrantRole {
  return typeof value === "string" && GRANT_ROLES.has(value as FormGrantRole);
}

export function formGrantRoleLabel(role: FormGrantRole): string {
  switch (role) {
    case "manage":
      return "Управление";
    case "view":
      return "Просмотр";
    default:
      return role;
  }
}

export function normalizeGrantedFormIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const ids = new Set<number>();
  for (const item of raw) {
    const id = readId(item);
    if (id != null) ids.add(id);
  }
  return [...ids];
}

export function normalizeFormsMeGrants(raw: unknown): {
  grantedFormIds: number[];
  manageGrantedFormIds: number[];
} {
  if (!raw || typeof raw !== "object") {
    return { grantedFormIds: [], manageGrantedFormIds: [] };
  }

  const item = raw as Record<string, unknown>;
  const grantedFormIds = normalizeGrantedFormIds(
    item.grantedFormIds ?? item.granted_form_ids,
  );
  const manageGrantedFormIds = normalizeGrantedFormIds(
    item.manageGrantedFormIds ?? item.manage_granted_form_ids,
  );

  const grantsRaw = item.grants ?? item.formGrants ?? item.form_grants;
  if (!Array.isArray(grantsRaw)) {
    return { grantedFormIds, manageGrantedFormIds };
  }

  const all = new Set(grantedFormIds);
  const manage = new Set(manageGrantedFormIds);

  for (const grant of grantsRaw) {
    if (!grant || typeof grant !== "object") continue;
    const row = grant as Record<string, unknown>;
    const formId = readId(row.formId ?? row.form_id);
    if (formId == null) continue;
    all.add(formId);
    if (isFormGrantRole(row.role) && row.role === "manage") {
      manage.add(formId);
    }
  }

  return {
    grantedFormIds: [...all],
    manageGrantedFormIds: [...manage],
  };
}

export function normalizeFormUserGrant(raw: unknown): FormUserGrantDto | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const id = readId(item.id);
  const userId = readId(item.userId ?? item.user_id);
  const chmId = readString(item.chmId ?? item.chm_id);
  const role = item.role;

  if (id == null || userId == null || !chmId || !isFormGrantRole(role)) {
    return null;
  }

  const inOrganisation = readBoolean(
    item.inOrganisation ?? item.in_organisation,
    true,
  );
  const warning = readString(item.warning);

  return {
    id,
    chmId,
    userId,
    name: readString(item.name),
    role,
    inOrganisation,
    warning: inOrganisation ? warning : warning ?? "Пользователь не состоит в организации формы",
    createdAt: readString(item.createdAt ?? item.created_at),
    updatedAt: readString(item.updatedAt ?? item.updated_at),
  };
}

export function normalizeFormUserGrants(raw: unknown): FormUserGrantDto[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeFormUserGrant)
      .filter((item): item is FormUserGrantDto => item != null);
  }

  if (raw && typeof raw === "object" && "data" in raw) {
    return normalizeFormUserGrants((raw as { data: unknown }).data);
  }

  return [];
}
